import {Component, Object3D, InputComponent, PhysXComponent} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {vec3} from 'gl-matrix';
import {Vignette} from './vignette.js';

/* Temporaries */

const _tempVecForward = vec3.create();
const _tempVecRight = vec3.create();
const _tempVecForce = vec3.create();

const _tempVecHeadPosition = vec3.create();
const _tempVecPlayerPosition = vec3.create();
const _tempVecEyeLeftPosition = vec3.create();
const _tempVecEyeRightPosition = vec3.create();
const _tempVecOffset = vec3.create();

/**
 * VR Locomotion component.
 * This component is used to move the player in VR based on Physx.
 *
 * @remarks
 * The component is dependent on an {@link InputComponent} component on the same object.
 */
export class Locomotion extends Component {
    static TypeName = 'locomotion';

    /**
     * The eye object is used to get the forward and right vectors of
     * where the player is looking.
     */
    @property.object({required: true})
    eyeLeft!: Object3D;

    @property.object({required: true})
    eyeRight!: Object3D;

    /**
     * The speed at which the player moves.
     */
    @property.float(12.0)
    speed = 12.0;

    /**
     * The deadzone threshold for the analog stick.
     */
    @property.float(0.2)
    deadzoneThreshold = 0.2;

    /**
     * The player physics object to apply the force to for movement.
     *
     * This is an Object3D containing an {@link PhysXComponent} component.
     */
    @property.object()
    playerPhysics?: Object3D;

    @property.object({required: true})
    trackedSpace!: Object3D;

    public disableRoomscaleCorrection = false;
    public isRotating = false;

    private physxComponent?: PhysXComponent;
    private input?: InputComponent;
    private vignette: Vignette | null = null;

    start() {
        if (!this.eyeLeft) {
            throw new Error(`locomotion(${this.object.name}):eyeLeft is not defined`);
        }
        if (!this.playerPhysics) {
            throw new Error(
                'The locomotion component requires a reference to an object with a PhysxComponent.'
            );
        }

        const tempInput = this.object.getComponent(InputComponent);
        if (!tempInput) {
            throw new Error(
                'The locomotion component requires an input component on the same object.'
            );
        }
        this.input = tempInput;

        const tempPhysxComponent = this.playerPhysics.getComponent(PhysXComponent);
        if (!tempPhysxComponent) {
            throw new Error('The locomotion component requires a physx component.');
        }
        this.physxComponent = tempPhysxComponent;

        const vignetteObject = this.eyeLeft.findByNameDirect('Vignette');
        if (vignetteObject.length != 0) {
            this.vignette = vignetteObject[0].getComponent(Vignette);
        } else {
            console.log('No vignette found in LeftEye children.');
        }
    }
    update() {
        if (!this.input?.xrInputSource?.gamepad?.axes?.length) {
            this.vignette?.stopMoving();
            return;
        }
        if (this.isRotating) {
            return;
        }

        const currentAxisX = this.input.xrInputSource.gamepad.axes[2];
        const currentAxisY = -this.input.xrInputSource.gamepad.axes[3];

        // remove deadzone to prevent drifting
        if (
            Math.abs(currentAxisX) < this.deadzoneThreshold &&
            Math.abs(currentAxisY) < this.deadzoneThreshold
        ) {
            this.vignette?.stopMoving();

            if (this.disableRoomscaleCorrection) {
                return;
            }

            if (!this.physxComponent!.kinematic) {
                this.physxComponent!.kinematic = true;
            }

            this.zeroCameraOffset();

            return;
        }

        this.physxComponent!.kinematic = false;

        // Get forward and right vectors from the eye object
        this.eyeLeft.getForwardWorld(_tempVecForward);
        this.eyeLeft.getRightWorld(_tempVecRight);

        // ignore the y axis
        _tempVecForward[1] = 0;
        _tempVecRight[1] = 0;

        vec3.normalize(_tempVecForward, _tempVecForward);
        vec3.normalize(_tempVecRight, _tempVecRight);

        // Scale vectors by speed
        vec3.scale(_tempVecForward, _tempVecForward, currentAxisY * this.speed);
        vec3.scale(_tempVecRight, _tempVecRight, currentAxisX * this.speed);

        // Add vectors to get final force

        vec3.add(_tempVecForce, _tempVecForward, _tempVecRight);

        // The Y axis of direction is set to zero and won't affect anything since
        // we are moving only in X-Z plane.
        _tempVecForce[1] = 0;

        // Apply force to the player to move it.
        this.physxComponent!.addForce(_tempVecForce);

        this.vignette?.startMoving();
    }

    zeroCameraOffset() {
        this.playerPhysics!.getPositionWorld(_tempVecPlayerPosition);
        this.eyeLeft.getPositionWorld(_tempVecEyeLeftPosition);
        this.eyeRight.getPositionWorld(_tempVecEyeRightPosition);

        vec3.add(_tempVecHeadPosition, _tempVecEyeLeftPosition, _tempVecEyeRightPosition);
        vec3.scale(_tempVecHeadPosition, _tempVecHeadPosition, 0.5);

        _tempVecHeadPosition[1] = _tempVecPlayerPosition[1];
        this.trackedSpace.getPositionWorld(_tempVecPlayerPosition);

        vec3.subtract(_tempVecOffset, _tempVecHeadPosition, _tempVecPlayerPosition);

        this.playerPhysics!.setPositionWorld([
            _tempVecHeadPosition[0],
            _tempVecPlayerPosition[1],
            _tempVecHeadPosition[2],
        ]);

        // translate trackedSpace in oposite direction
        this.trackedSpace.setPositionWorld([
            _tempVecHeadPosition[0] - _tempVecOffset[0],
            _tempVecPlayerPosition[1],
            _tempVecHeadPosition[2] - _tempVecOffset[2],
        ]);
    }
}
